import { useTranslation } from "react-i18next";

import AnalyticsCard from "@/features/certifications/analytics/components/AnalyticsCard";
import { LIGHT_TRACK_SLUG } from "@/features/certifications/analytics/constants";
import {
  countSwitches,
  getTrackLabel,
  getTrackShares,
  getTrackSwitchRows,
} from "@/features/certifications/analytics/utils";
import { PathSplitCardProps } from "@/features/certifications/analytics/types";

import "../styles.css";

const PathSplitCard = ({
  certification,
  data,
  isLoading,
  isError,
}: PathSplitCardProps) => {
  const { t } = useTranslation();
  const shares = data ? getTrackShares(data) : [];
  const getLabel = (trackId: number, fallback: string) =>
    getTrackLabel(t, certification, trackId, fallback);
  const switches = data ? getTrackSwitchRows(data, getLabel) : [];
  const getColorClass = (slug: string) =>
    slug === LIGHT_TRACK_SLUG ? "analytics-path-light" : "analytics-path-dark";

  return (
    <AnalyticsCard
      title={t("certification.admin.analytics.paths.title")}
      isLoading={isLoading}
      isError={isError}
    >
      <div className="flex flex-col gap-300">
        <div className="analytics-stack flex full-width" aria-hidden>
          {shares.map((share) => (
            <span
              key={share.id}
              className={getColorClass(share.slug)}
              style={{ width: `${share.percent}%` }}
            />
          ))}
        </div>

        <ul className="analytics-list flex flex-col gap-200">
          {shares.map((share) => (
            <li
              key={share.id}
              className="flex items-center justify-between gap-200 body-s-regular"
            >
              <span className="flex items-center gap-200">
                <span
                  className={`analytics-swatch ${getColorClass(share.slug)}`}
                  aria-hidden
                />

                {getLabel(share.id, share.title)}
              </span>

              <span className="analytics-value body-s-medium">
                {share.count}
              </span>
            </li>
          ))}
        </ul>

        <div className="analytics-footer flex flex-col gap-200">
          <div className="flex items-center justify-between gap-200 body-s-regular">
            <span>{t("certification.admin.analytics.paths.switched")}</span>

            <span className="analytics-value body-s-medium">
              {data ? countSwitches(data) : 0}
            </span>
          </div>

          {switches.length > 0 && (
            <ul className="analytics-list flex flex-col gap-100">
              {switches.map((row) => (
                <li
                  key={row.key}
                  className="analytics-muted flex items-center justify-between gap-200 body-xs-regular"
                >
                  <span>
                    {t("certification.admin.analytics.paths.switch", {
                      from: row.from,
                      to: row.to,
                    })}
                  </span>

                  <span>{row.switches}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </AnalyticsCard>
  );
};

export default PathSplitCard;
